<template>
  <div class="w-full h-full bg-slate-900 border-r border-slate-800 flex flex-col select-none">
    <!-- Sidebar Header & Search -->
    <div class="p-3 border-b border-slate-800 space-y-2.5">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-slate-400">UNIBO Exam Bank</span>
        <div class="flex items-center gap-1.5">
          <span class="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">
            {{ filteredQuestions.length }} / {{ questions.length }}
          </span>
          <button
            type="button"
            class="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-300 transition-colors cursor-pointer"
            title="Hide Question Bank (Alt+B)"
            @click="$emit('collapse')"
          >
            <i class="pi pi-angle-double-left text-xs"></i>
          </button>
        </div>
      </div>

      <!-- Search Input -->
      <div class="relative">
        <i class="pi pi-search absolute left-3 top-2.5 text-slate-400 text-sm"></i>
        <input 
          v-model="searchQuery" 
          type="text" 
          placeholder="Search questions, algorithms..." 
          class="w-full bg-slate-800 border border-slate-700/80 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-unibo-red focus:ring-1 focus:ring-unibo-red"
        />
        <button 
          v-if="searchQuery" 
          @click="searchQuery = ''" 
          class="absolute right-2.5 top-2 text-slate-400 hover:text-slate-200"
        >
          <i class="pi pi-times text-xs"></i>
        </button>
      </div>

      <!-- Status Filter Pill Buttons -->
      <div class="grid grid-cols-4 gap-1 bg-slate-950/60 p-1 rounded-lg text-xs font-medium">
        <button 
          v-for="st in statusOptions" 
          :key="st.value"
          @click="selectedStatus = st.value"
          :class="[
            'py-1 rounded text-center transition-colors',
            selectedStatus === st.value 
              ? 'bg-slate-800 text-slate-100 shadow-sm border border-slate-700' 
              : 'text-slate-400 hover:text-slate-200'
          ]"
        >
          {{ st.label }}
        </button>
      </div>
    </div>

    <!-- Categories Tab Strip -->
    <div class="flex overflow-x-auto border-b border-slate-800 bg-slate-950/40 p-1.5 gap-1 no-scrollbar">
      <button 
        v-for="cat in categoryTabs" 
        :key="cat.id"
        @click="selectedCategory = cat.id"
        :class="[
          'px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors flex items-center space-x-1',
          selectedCategory === cat.id 
            ? 'bg-unibo-red/20 text-unibo-red border border-unibo-red/30 font-bold' 
            : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
        ]"
      >
        <span>{{ cat.label }}</span>
        <span class="text-[10px] opacity-75 font-mono">({{ getCategoryCount(cat.id) }})</span>
      </button>
    </div>

    <!-- Questions List -->
    <div class="flex-1 overflow-y-auto p-2 space-y-1.5">
      <div 
        v-if="filteredQuestions.length === 0" 
        class="text-center py-10 px-4 text-slate-500 text-xs"
      >
        <i class="pi pi-inbox text-2xl mb-2 text-slate-600 block"></i>
        No exam questions match your search filters.
      </div>

      <div 
        v-for="q in filteredQuestions" 
        :key="q.id"
        @click="$emit('select-question', q.id)"
        :class="[
          'p-3 rounded-lg border text-left cursor-pointer transition-all relative group select-none',
          activeQuestionId === q.id 
            ? 'bg-slate-800/90 border-unibo-red/60 shadow-md ring-1 ring-unibo-red/30' 
            : 'bg-slate-900/60 border-slate-800/90 hover:bg-slate-800/50 hover:border-slate-700'
        ]"
      >
        <!-- Top row: Category tag & Status icon -->
        <div class="flex items-center justify-between mb-1.5 pointer-events-none">
          <span 
            class="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded tracking-wide"
            :class="getCategoryStyle(q.category)"
          >
            {{ q.category }}
          </span>

          <div class="flex items-center space-x-1.5">
            <span 
              class="text-[10px] px-1.5 py-0.5 rounded font-mono font-medium"
              :class="getDifficultyStyle(q.difficulty)"
            >
              {{ q.difficulty }}
            </span>
            <i :class="getStatusIcon(getAnswerStatus(q.id))"></i>
          </div>
        </div>

        <!-- Question Title -->
        <h3 class="text-xs font-semibold text-slate-200 group-hover:text-white line-clamp-2 leading-snug pointer-events-none">
          {{ q.title }}
        </h3>

        <!-- Tags & Exam Date -->
        <div class="mt-2 flex items-center justify-between text-[10px] text-slate-400 font-mono pointer-events-none">
          <span v-if="q.examDate" class="text-slate-500">
            <i class="pi pi-calendar text-[9px] mr-1"></i>{{ q.examDate }}
          </span>
          <span v-else class="text-slate-500">UNIBO Exam</span>

          <span class="truncate max-w-[120px] text-right opacity-80">
            {{ q.tags[0] }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Question, Category, StudentStatus } from '../types';

const props = defineProps<{
  questions: Question[];
  activeQuestionId: string;
  answerStatuses: Record<string, StudentStatus>;
}>();

defineEmits(['select-question', 'collapse']);

const searchQuery = ref('');
const selectedCategory = ref<string>('all');
const selectedStatus = ref<string>('all');

const categoryTabs = [
  { id: 'all', label: 'All' },
  { id: 'clustering', label: 'Clustering' },
  { id: 'classification', label: 'Classification' },
  { id: 'regression', label: 'Regression' },
  { id: 'association-rules', label: 'Assoc. Rules' },
];

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'not_started', label: 'Todo' },
  { value: 'in_progress', label: 'Doing' },
  { value: 'done', label: 'Done' }
];

function getAnswerStatus(qId: string): StudentStatus {
  return props.answerStatuses[qId] || 'not_started';
}

function getCategoryCount(catId: string): number {
  if (catId === 'all') return props.questions.length;
  return props.questions.filter(q => q.category === catId).length;
}

const filteredQuestions = computed(() => {
  return props.questions.filter(q => {
    if (selectedCategory.value !== 'all' && q.category !== selectedCategory.value) {
      return false;
    }

    const status = getAnswerStatus(q.id);
    if (selectedStatus.value !== 'all' && status !== selectedStatus.value) {
      return false;
    }

    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase();
      const matchTitle = q.title.toLowerCase().includes(query);
      const matchTags = q.tags.some(t => t.toLowerCase().includes(query));
      const matchPrompt = q.promptMarkdown.toLowerCase().includes(query);
      if (!matchTitle && !matchTags && !matchPrompt) {
        return false;
      }
    }

    return true;
  });
});

function getCategoryStyle(category: Category) {
  switch (category) {
    case 'clustering': return 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20';
    case 'classification': return 'bg-purple-500/10 text-purple-400 border border-purple-500/20';
    case 'regression': return 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
    case 'association-rules': return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
    default: return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
  }
}

function getDifficultyStyle(difficulty: string) {
  switch (difficulty) {
    case 'easy': return 'bg-emerald-500/10 text-emerald-400';
    case 'medium': return 'bg-amber-500/10 text-amber-400';
    case 'hard': return 'bg-rose-500/10 text-rose-400';
    default: return 'bg-slate-500/10 text-slate-400';
  }
}

function getStatusIcon(status: StudentStatus) {
  switch (status) {
    case 'done': return 'pi pi-check-circle text-emerald-400 text-sm';
    case 'in_progress': return 'pi pi-clock text-amber-400 text-sm';
    case 'not_started': return 'pi pi-circle text-slate-600 text-sm';
  }
}
</script>
